import { type ApolloClient, useApolloClient } from '@apollo/client';
import { toM3 } from '../../../constants/VolumeUnits';
import type { PaymentUnit } from '../../../graphql';
import {
  type FreightCostsQuery,
  type FreightCostsQueryVariables,
  FreightCostsDocument,
  FreightType,
} from '../../../graphql';
import { isDefined } from '../../../lib/util';
import { type EventTimeline } from '../../core/Timelines';
import { useCallback, useEffect, useState } from 'react';
import type {
  CoaEvent,
  CoreEventType,
  LoadEvent,
  SpotEvent,
  TCOrderEvent,
  TimeData,
} from '../../core/TimeData';
import { isLoadEventTimeData } from '../../core/TimeData';
import type { ScenarioOrder } from '../../../lib/planningData';
import { EventCategory, getShipWeightCapacity } from '../../core/Core';
import { getOrderCoaDetails } from '../getOderCoaDetails';
import {
  getMaxCoaFreightRateByCoaId,
  prepareCoaInput,
} from './maxCoaFreightRate';
import { convertToDefaultCurrency } from '../../../state/zustand';
import { stringToNumOpt } from '../../core/util';
import type { OrderPorts } from '../../../lib/worldscaleFreight';
import { type WorldscaleFreightRateMap } from '../../../lib/worldscaleFreight';
import { calculateFreightAmount } from '../../../lib/freightCalculation';
import type { ClientId } from '../../core/ClientId';
import { getTimelineLoadedAmounts } from '../loadedAmounts';
import type {
  ScenarioShipmentExtraFreight,
  ScenarioShipmentFreight,
} from '../../Scenario';

export interface FreightCostParameter {
  name: string;
  value: string;
}
export interface FreightCost {
  orderId: number | null;
  source: string;
  value: number;
  parameters: FreightCostParameter[];
}

export type FreightCosts = FreightCost[];

export const getFreightCost = (
  mass: number | null,
  volume: number | null,
  pcs: number | null,
  freightAmount: number,
  freightUnit: PaymentUnit,
  worldscaleFreightRates?: WorldscaleFreightRateMap,
  order?: OrderPorts,
): number | null => {
  return calculateFreightAmount({
    mass,
    volume,
    pcs,
    freightRate: freightAmount,
    freightUnit,
    worldscaleFreightRates,
    order,
  });
};

export function calculateFreightCosts(
  loadEvents: TimeData<LoadEvent>[],
  freightCostsData: FreightCostsQuery,
  worldscaleFreightRates?: WorldscaleFreightRateMap,
) {
  const orderById = new Map(
    freightCostsData.allOrders?.nodes
      ?.filter(isDefined)
      .map((order) => [order.id, order]),
  );

  const freightCosts: FreightCosts = [];

  const maxCoaFreightRateByCoaId = getMaxCoaFreightRateByCoaId(
    loadEvents,
    orderById,
    freightCostsData.allCoaContracts,
  );

  const ignoredVoyageCharterBecauseAtLeastOneOrderIsCoa = new Set<number>();
  const voyageChartersToProcessLumpsumsByVCID = new Map<
    number,
    NonNullable<
      NonNullable<
        NonNullable<
          NonNullable<
            NonNullable<(typeof freightCostsData)['allOrders']>['nodes']
          >[number]
        >['orderVoyageCharterRelationsByOrderId']['nodes'][number]
      >['voyageCharterByVoyageCharterId']
    >
  >();

  for (const loadEvent of loadEvents) {
    const order = orderById.get(loadEvent.event.order.id);
    if (!order) {
      console.warn(
        `calculateFreightCosts here: this function expects to reveice related orders`,
      );
      continue;
    }

    // For regular TC events (incoming), skip freight costs as cargo is transferred on own vessel
    // For relet TC events (outgoing), we need to calculate freight costs from the outgoing voyage charter
    const isReletEvent =
      loadEvent.event.category === EventCategory.TC &&
      order.orderVoyageCharterRelationsByOrderId?.nodes.length > 0;

    if (loadEvent.event.category === EventCategory.TC && !isReletEvent) {
      // No freight costs if cargo is transferred on the own vessel even if VC exists.
      // This can happen when user is comparing SPOT vs TC assignment in scenario.
      // If freight is entered in "Spot contract" tab and saved then VC with a link to order is created immediately
      continue;
    }

    const voyageCharterRelation =
      order.orderVoyageCharterRelationsByOrderId?.nodes.at(0);
    const voyageCharter = voyageCharterRelation?.voyageCharterByVoyageCharterId;
    const voyageCharterCurrency = voyageCharter?.currency;
    const freightFromVoyageCharter =
      voyageCharterRelation?.voyageCharterOrderFreightsByOrderVoyageCharterRelationId?.nodes.at(
        0,
      );
    const voyageCharterFreightAmount =
      freightFromVoyageCharter?.freightAmount ?? null;
    const isVoyageCharterFreightAmountValid =
      voyageCharterFreightAmount !== null && +voyageCharterFreightAmount > 0;

    /** The COA the event is currently assigned to */
    const assignedCoa =
      loadEvent.event.category === EventCategory.COA
        ? freightCostsData?.allCoaContracts?.nodes.find(
            (coa) =>
              loadEvent.event.category === EventCategory.COA &&
              coa?.id === loadEvent.event.entry.coaId,
          )
        : null;

    /*
     * Use voyage charter in COA case only if is is the same COA contract and
     * the same vessel and the voyage charter has proper freight rate defined
     */
    const isCoaOverriddenByVoyageCharter =
      loadEvent.event.category === EventCategory.COA &&
      loadEvent.event.entry.vesselId === voyageCharter?.vesselId &&
      isVoyageCharterFreightAmountValid &&
      assignedCoa?.id ===
        voyageCharter?.voyageCharterToContractsByVoyageCharterId.nodes.at(0)
          ?.coaContractByCoaId?.id;

    const { coaAssortment, coaFreight } = !assignedCoa
      ? {
          coaAssortment: null,
          coaFreight: null,
        }
      : getOrderCoaDetails(
          {
            assortmentId: order.assortmentId ?? null,
            assortmentGroupId:
              order.assortmentByAssortmentId?.assortmentGroup ?? null,
            loadPortId: order.loadPortId ?? null,
            dischargePortId: order.dischargePortId,
          },
          { mass: loadEvent.mass, volume: loadEvent.volume },
          prepareCoaInput(assignedCoa),
          voyageCharterRelation?.coaRouteId,
        );

    const useCoaRate =
      coaFreight &&
      (!isCoaOverriddenByVoyageCharter || assignedCoa?.useHighestFreightRate);

    const freight = useCoaRate
      ? {
          freightAmount:
            (assignedCoa && maxCoaFreightRateByCoaId.get(assignedCoa.id)) ??
            coaFreight.price,
          freightUnit: coaAssortment?.paymentUnit,
        }
      : freightFromVoyageCharter;

    const measurement = order.cargoMeasurementsByOrderId?.nodes[0] ?? undefined;

    const { freightAmount, freightUnit } = freight ?? {};

    const plannedFreightValue =
      freightAmount && freightUnit
        ? getFreightCost(
            loadEvent.mass,
            loadEvent.volume,
            loadEvent.pcs,
            +freightAmount,
            freightUnit,
            worldscaleFreightRates,
            loadEvent.event.order,
          )
        : null;

    const currency =
      (isCoaOverriddenByVoyageCharter || !assignedCoa
        ? voyageCharterCurrency
        : undefined) ??
      (!isCoaOverriddenByVoyageCharter && assignedCoa
        ? assignedCoa.currency
        : undefined) ??
      'EUR';

    const plannedFreight = plannedFreightValue
      ? convertToDefaultCurrency(plannedFreightValue, currency)
      : null;

    const measuredFreight = measurement
      ? (() => {
          const { measuredVolume, measuredWeight, volumeUnit, weightUnit } =
            measurement;
          const mrMass =
            measuredWeight && weightUnit
              ? getShipWeightCapacity(+measuredWeight, weightUnit)
              : null;
          const mrVolume =
            measuredVolume && volumeUnit
              ? toM3(+measuredVolume, volumeUnit)
              : null;

          const measuredFreightValue =
            freightAmount && freightUnit
              ? getFreightCost(
                  mrMass,
                  mrVolume,
                  null,
                  +freightAmount,
                  freightUnit,
                  worldscaleFreightRates,
                  loadEvent.event.order,
                )
              : null;

          return measuredFreightValue
            ? convertToDefaultCurrency(
                measuredFreightValue,
                voyageCharterCurrency ?? 'EUR',
              )
            : null;
        })()
      : null;

    const orderSourceText = `Order #${order.id} ${
      order?.orderDetailsByOrderId?.nodes[0]?.orderName || ''
    }`;

    freightCosts.push({
      orderId: order.id,
      source: orderSourceText + ' freight',
      value: measuredFreight ?? plannedFreight ?? 0,
      parameters: [
        plannedFreight
          ? {
              name: 'Planned freight',
              value: `${plannedFreight}`,
            }
          : null,
        measuredFreight
          ? {
              name: 'Measured freight',
              value: `${measuredFreight}`,
            }
          : null,
        freightAmount
          ? {
              name: 'Freight amount',
              value: `${stringToNumOpt(freightAmount) ?? ''}`,
            }
          : null,
        freightUnit
          ? {
              name: 'Freight unit',
              value: `${freightUnit}`,
            }
          : null,
        currency
          ? {
              name: 'Currency',
              value: `${currency}`,
            }
          : null,
      ].filter(isDefined),
    });

    if (!assignedCoa || isCoaOverriddenByVoyageCharter) {
      voyageCharter?.voyageCharterExtraFreightsByVoyageCharterId?.nodes
        ?.filter(isDefined)
        .forEach((extraFreight) => {
          if (extraFreight.type !== FreightType.RATE) {
            return;
          }
          if (!freightUnit) {
            return;
          }
          const extraCostAmount = getFreightCost(
            loadEvent.mass,
            loadEvent.volume,
            null,
            +extraFreight.amount,
            freightUnit,
            worldscaleFreightRates,
            loadEvent.event.order,
          );
          if (!extraCostAmount) {
            return;
          }
          freightCosts.push({
            orderId: order.id,
            source: orderSourceText + ' extra cost rate',
            value: extraCostAmount,
            parameters: [
              extraFreight.name
                ? {
                    name: 'Name',
                    value: extraFreight.name,
                  }
                : null,
              extraFreight.description
                ? {
                    name: 'Description',
                    value: extraFreight.description,
                  }
                : null,
              {
                name: 'Amount',
                value: `${extraFreight.amount}`,
              },
            ].filter(isDefined),
          });
        });
    }
    if (assignedCoa && !isCoaOverriddenByVoyageCharter && voyageCharter) {
      ignoredVoyageCharterBecauseAtLeastOneOrderIsCoa.add(voyageCharter.id);
    }

    if (
      voyageCharter &&
      !voyageChartersToProcessLumpsumsByVCID.has(voyageCharter.id) &&
      !ignoredVoyageCharterBecauseAtLeastOneOrderIsCoa.has(voyageCharter.id)
    ) {
      voyageChartersToProcessLumpsumsByVCID.set(
        voyageCharter.id,
        voyageCharter,
      );
    }
  }

  for (const voyageCharter of voyageChartersToProcessLumpsumsByVCID.values()) {
    const vcText =
      [voyageCharter.externalId, voyageCharter.name]
        .filter((x) => x)
        .join(' ') || 'Contract';

    voyageCharter.voyageCharterExtraFreightsByVoyageCharterId?.nodes
      ?.filter(isDefined)
      .forEach((extraFreight) => {
        if (extraFreight.type !== FreightType.LUMPSUM) {
          return;
        }

        if (!extraFreight.amount) {
          return;
        }

        freightCosts.push({
          orderId: null,
          source: vcText + ' extra freight lumpsum',
          value: +extraFreight.amount,
          parameters: [
            extraFreight.name
              ? {
                  name: 'Name',
                  value: extraFreight.name,
                }
              : null,
            extraFreight.description
              ? {
                  name: 'Description',
                  value: extraFreight.description,
                }
              : null,
            {
              name: 'Amount',
              value: `${extraFreight.amount}`,
            },
          ].filter(isDefined),
        });
      });

    if (voyageCharter.lumpsum) {
      freightCosts.push({
        orderId: null,
        source: vcText + ' total freight',
        value: +voyageCharter.lumpsum,
        parameters: [].filter(isDefined),
      });
    }
  }

  return freightCosts;
}

export function calculateShipmentFreightCosts(
  timeline: EventTimeline,
  orders: ScenarioOrder[] | undefined,
  shipmentFreightByShipmentId: Map<ClientId, ScenarioShipmentFreight>,
  shipmentExtraFreightsByShipmentId: Map<
    ClientId,
    Array<ScenarioShipmentExtraFreight>
  >,
): {
  shipmentFreightCosts: FreightCost[];
  orderIdsWithShipmentFreight: Set<number>;
} {
  const costs: FreightCost[] = [];
  const orderIdsWithOverride = new Set<number>();
  const loadedAmounts = getTimelineLoadedAmounts(timeline);

  const loadedByShipment = new Map<
    ClientId,
    { mass: number; volume: number; pcs: number; orderIds: number[] }
  >();

  for (const la of loadedAmounts) {
    const order = orders?.find((o) => o.id === la.orderId);
    if (!order?.shipmentId) continue;

    const existing = loadedByShipment.get(order.shipmentId) ?? {
      mass: 0,
      volume: 0,
      pcs: 0,
      orderIds: [],
    };
    existing.mass += la.loadedMass;
    existing.volume += la.loadedVolume;
    existing.pcs += la.loadedPcs;
    existing.orderIds.push(la.orderId);
    loadedByShipment.set(order.shipmentId, existing);
  }

  shipmentFreightByShipmentId.forEach((freight, shipmentId) => {
    const loaded = loadedByShipment.get(shipmentId);
    if (!loaded) return;

    loaded.orderIds.forEach((id) => orderIdsWithOverride.add(id));

    const costAmount = calculateFreightAmount({
      mass: loaded.mass,
      volume: loaded.volume,
      pcs: loaded.pcs,
      freightRate: Number(freight.freightAmount),
      freightUnit: freight.freightUnit.toUpperCase() as PaymentUnit,
      worldscaleFreightRates: undefined,
      order: undefined,
    });

    if (costAmount) {
      costs.push({
        orderId: null,
        source: 'Shipment Freight',
        value: costAmount,
        parameters: [
          { name: 'Freight rate', value: `${freight.freightAmount}` },
          { name: 'Freight unit', value: freight.freightUnit },
        ],
      });
    }
  });

  shipmentExtraFreightsByShipmentId.forEach((extraFreights, shipmentId) => {
    const loaded = loadedByShipment.get(shipmentId);
    if (!loaded) return;

    for (const ef of extraFreights) {
      const costAmount = calculateFreightAmount({
        mass: loaded.mass,
        volume: loaded.volume,
        pcs: loaded.pcs,
        freightRate: Number(ef.freightAmount),
        freightUnit: ef.freightUnit,
        worldscaleFreightRates: undefined,
        order: undefined,
      });

      if (costAmount) {
        costs.push({
          orderId: null,
          source: 'Shipment Extra Freight',
          value: costAmount,
          parameters: [
            { name: 'Amount', value: `${ef.freightAmount}` },
            { name: 'Unit', value: ef.freightUnit },
            ef.description
              ? { name: 'Description', value: ef.description }
              : null,
          ].filter(isDefined),
        });
      }
    }
  });

  return {
    shipmentFreightCosts: costs,
    orderIdsWithShipmentFreight: orderIdsWithOverride,
  };
}

export async function fetchFreightCosts(
  loadEvents: TimeData<
    | (TCOrderEvent & {
        type: CoreEventType.Load;
        order: ScenarioOrder;
      })
    | (SpotEvent & {
        type: CoreEventType.Load;
        order: ScenarioOrder;
      })
    | (CoaEvent & {
        type: CoreEventType.Load;
        order: ScenarioOrder;
      })
  >[],
  apolloClient: ApolloClient<object>,
) {
  const { data } = await apolloClient.query<
    FreightCostsQuery,
    FreightCostsQueryVariables
  >({
    query: FreightCostsDocument,
    variables: {
      orderIds: loadEvents.map((e) => e.event.order.id),
    },
    fetchPolicy: 'no-cache',
  });

  const freightCosts = calculateFreightCosts(loadEvents, data);

  return {
    freightCosts,
  };
}

export const useTimelineFreightCosts = (timeline: EventTimeline) => {
  const apolloClient = useApolloClient();
  const [costsLoading, setCostsLoading] = useState<boolean>(false);
  const [freightCosts, setFreightCosts] = useState<FreightCosts | null>(null);

  const doGetTimelineFreightCosts = useCallback(async () => {
    setCostsLoading(true);

    const loadEvents = timeline.voyageGroups.flatMap((vg) =>
      vg.events.filter(isLoadEventTimeData),
    );

    const costs = await fetchFreightCosts(loadEvents, apolloClient);

    setCostsLoading(false);

    if (costs.freightCosts) {
      setFreightCosts(costs.freightCosts);
    }
  }, [timeline, apolloClient]);

  useEffect(() => {
    doGetTimelineFreightCosts();
  }, [doGetTimelineFreightCosts]);

  return {
    loading: costsLoading,
    freightCosts,
    refetch: doGetTimelineFreightCosts,
  };
};
